// src/Pages/AdminManagment/EditAdmin.jsx
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { closeModal } from "../../redux/features/modalSlice";
import { useUpdateAdminMutation } from "../../redux/services/sabAdminService/admin.Services";
import { useGetWaadDropdownQuery } from "../../redux/services/waadService/waad.Services";
import { successToast, errorToast } from "../../Utils/toast";

function EditAdmin({ data }) {
  const dispatch = useDispatch();
  const [updateAdmin, { isLoading }] = useUpdateAdminMutation();
  const { data: waadData, isLoading: waadLoading } = useGetWaadDropdownQuery();

  // ✅ Initial State
  const [formData, setFormData] = useState({
    id: data?.id,
    name: data?.name || "",
    email: data?.email || "",
    mobile: data?.mobile || "",
    waad_id: data?.waad_id || "",
    waad_name: data?.waad_name || "",
    role: data?.role || "sub_admin", // Admin / Sub Admin
    status: data?.status || 1, // 1 for Active, 0 for Inactive
  });

  const [errors, setErrors] = useState({});

  // ✅ Auto-fill waad_name when waad_id changes
  useEffect(() => {
    if (formData.waad_id) {
      const selectedWaad = waadData?.data?.find(w => w.id == formData.waad_id);
      if (selectedWaad) {
        setFormData(prev => ({
          ...prev,
          waad_name: selectedWaad.waad_name
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        waad_name: ""
      }));
    }
  }, [formData.waad_id, waadData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;
    if (name === "status") {
      finalValue = parseInt(value);
    }
    setFormData({
      ...formData,
      [name]: finalValue,
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Admin name is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (formData.mobile && !/^[0-9]{10}$/.test(formData.mobile)) {
      newErrors.mobile = "Mobile must be 10 digits";
    }
    if (formData.role === "sub_admin" && !formData.waad_id) {
      newErrors.waad_id = "Please select a waad for sub admin";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdateAdmin = async () => {
    if (!validate()) return;

    try {
      // ✅ सभी undefined values को null में बदलें
      const payload = {
        id: formData.id, // ✅ id को body में जरूर भेजें
        name: formData.name || null,
        email: formData.email || null,
        mobile: formData.mobile || null,
        waad_id: formData.role === "sub_admin" ? parseInt(formData.waad_id) || null : null,
        waad_name: formData.role === "sub_admin" ? formData.waad_name || null : null,
        role: formData.role || null,
        status: formData.status !== undefined ? parseInt(formData.status) : 1,
      };

      // ✅ Update API Call
      const response = await updateAdmin(payload).unwrap();
      
      successToast(response?.message || "Admin updated successfully");
      dispatch(closeModal());
    } catch (error) {
      console.error("Update error:", error);
      const errorMsg = error?.data?.message || error?.message || "Update Failed";
      errorToast(errorMsg);
    }
  };

  return (
  <div className="admin-modal-wrapper">
  <div className="model-custum">

    <div className="form-group">
      <label className="form-label">
        Admin Name <span>*</span>
      </label>
      <input
        type="text"
        name="name"
        value={formData.name}
        onChange={handleInputChange}
        className={`form-control-custom ${errors.name ? "error" : ""}`}
        placeholder="Enter Admin Name"
      />
      {errors.name && <div className="error-text">{errors.name}</div>}
    </div>

    <div className="form-group">
      <label className="form-label">
        Email <span>*</span>
      </label>
      <input
        type="email"
        name="email"
        value={formData.email}
        onChange={handleInputChange}
        className={`form-control-custom ${errors.email ? "error" : ""}`}
        placeholder="Enter Email"
      />
      {errors.email && <div className="error-text">{errors.email}</div>}
    </div>

    <div className="form-group">
      <label className="form-label">Mobile</label>
      <input
        type="text"
        name="mobile"
        value={formData.mobile}
        onChange={handleInputChange}
        className={`form-control-custom ${errors.mobile ? "error" : ""}`}
        placeholder="Enter Mobile Number"
      />
      {errors.mobile && <div className="error-text">{errors.mobile}</div>}
    </div>

    <div className="form-group">
      <label className="form-label">
        Role <span>*</span>
      </label>
      <select
        name="role"
        value={formData.role}
        onChange={handleInputChange}
        className="form-control-custom"
      >
        <option value="admin">Admin</option>
        <option value="sub_admin">Sub Admin</option>
      </select>

      <div className="form-helper">
        {formData.role === "admin" && "Full access to everything"}
        {formData.role === "sub_admin" &&
          "Limited access with permissions"}
      </div>
    </div>

    {formData.role === "sub_admin" && (
      <div className="form-group">
        <label className="form-label">
          Select Waad <span>*</span>
        </label>

        <select
          name="waad_id"
          value={formData.waad_id}
          onChange={handleInputChange}
          className={`form-control-custom ${
            errors.waad_id ? "error" : ""
          }`}
        >
          <option value="">Select Waad</option>

          {waadData?.data?.map((waad) => (
            <option key={waad.id} value={waad.id}>
              {waad.waad_name}
            </option>
          ))}
        </select>

        {errors.waad_id && (
          <div className="error-text">{errors.waad_id}</div>
        )}
      </div>
    )}

    <div className="form-group">
      <label className="form-label">Status</label>

      <select
        name="status"
        value={formData.status}
        onChange={handleInputChange}
        className="form-control-custom"
      >
        <option value={1}>Active</option>
        <option value={0}>Inactive</option>
      </select>
    </div>

    <div className="modal-footer-custom">
      <button
        className="btn-cancel"
        onClick={() => dispatch(closeModal())}
      >
        Cancel
      </button>

      <button
        className="btn-save"
        onClick={handleUpdateAdmin}
      >
        {isLoading ? "Updating..." : "Save Changes"}
      </button>
    </div>

  </div>
</div>
  );
}

export default EditAdmin;